import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useLocalization } from '@lib/localization';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { pendingDotStyles } from '@pages/AppSettings.styles';
import { countPillStyles, typeIconStyles, typeRowStyles } from '@pages/CustomPoiTypes.styles';
import { resolveLabel, type PoiType } from '@store/api/poiTypeEndpoints';
import { AngleRightIcon, MapMarkerIcon } from 'react-line-awesome';

interface Props {
  poiType: PoiType;
  /** Whether the reader left changes to it in the designer that are not saved: they are there again when it is opened. */
  unsaved: boolean;
  onOpen: () => void;
}

/**
 * One line of the catalogue. Deliberately just an identity and a size — everything that
 * can be edited lives on the type's own page, the way a team row opens a team. A type the
 * reader left unsaved changes to carries the mark the owner picker of the app settings puts
 * on an owner.
 */
const PoiTypeRow: React.FC<Props> = ({ poiType, unsaved, onOpen }) => {
  const { t } = useLocalization();
  const locales = useCustomizationLocales();

  const fieldCount = poiType.fields.length
    + poiType.groups.reduce((total, group) => total + group.fields.length, 0);

  return (
    <Stack direction="row" spacing={2} sx={typeRowStyles} onClick={onOpen}>
      <Box sx={typeIconStyles}>
        <MapMarkerIcon />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontWeight: 600 }} noWrap>{resolveLabel(poiType.name, locales)}</Typography>
          {/* A dot says nothing to a screen reader, so it is named; the tooltip says the same to a pointer. */}
          {unsaved && (
            <Tooltip title={t('Customization_Designer_Unsaved')} arrow>
              <Box component="span" role="img" aria-label={t('Customization_Designer_Unsaved')} sx={pendingDotStyles} />
            </Tooltip>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" noWrap>{poiType.description}</Typography>
      </Box>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
        <Box component="span" sx={countPillStyles}>
          {t('Customization_Group_Count', { count: poiType.groups.length })} · {t('Customization_Field_Count', { count: fieldCount })}
        </Box>
        <Box component="span" sx={countPillStyles}>v{poiType.version}</Box>
        <Box sx={{ color: 'text.secondary', display: 'flex', fontSize: 18 }}>
          <AngleRightIcon />
        </Box>
      </Stack>
    </Stack>
  );
};

export default PoiTypeRow;
